import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLeadFormSubmit } from '@/hooks/useLeadFormSubmit';
import { inputClass, labelClass } from '@/components/forms/leadFormStyles';

const WAVES = [
  {
    n: 'W0',
    title: 'Day one — zero new credentials',
    description:
      'Already running an agent registry (Microsoft Agent 365, ServiceNow AI Control Tower, MuleSoft Agent Fabric)? We read that first — your existing inventory is on screen the same day, before you authorize anything new.',
  },
  {
    n: 'W1',
    title: 'Week 1 — one cloud account',
    description:
      'A single read-only credential on your primary AI platform — Bedrock or Azure AI Foundry. One account, one owner to ask. First agents on screen about 30 minutes after the credential.',
  },
  {
    n: 'W2',
    title: 'Week 2 — identity & directory',
    description:
      'Non-human identities holding LLM grants (Entra, Okta), correlated against Wave 1 — so ambiguous service principals resolve instead of piling up. This is where the unregistered and unowned agents appear.',
  },
  {
    n: 'W3',
    title: 'Weeks 3–4 — code & telemetry',
    description:
      'Repos and CI for homegrown agents; agent traces in telemetry you already collect. Network and endpoint sweeps only if you want them. Every agent risk-ranked, with a data-access map.',
  },
];

const EASY_YES_ITEMS = [
  {
    title: 'Read-only, by construction',
    description:
      'View-only scopes only. We publish the exact IAM policy for every connector — check it line by line.',
  },
  {
    title: 'Metadata only',
    description: 'Never prompts, source code, secret values, or business data.',
  },
  {
    title: 'Nothing deployed',
    description:
      'No sidecars, no gateways, no network taps. Discovery runs from our side of the API.',
  },
  {
    title: 'Revocable in one click',
    description:
      'You issue the credentials; you revoke them. Your data package is deleted on request.',
  },
];

const PLATFORM_OPTIONS = [
  'Anthropic',
  'AWS Bedrock',
  'Azure AI Foundry',
  'Google Vertex AI',
  'Copilot Studio',
  'Salesforce Agentforce',
  'Multiple / other',
  'Not sure yet',
];

export function TrialPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    platform: PLATFORM_OPTIONS[0],
    website: '', // honeypot field
  });

  const { isSubmitting, status: submitStatus, errorMessage, submit, reset } = useLeadFormSubmit();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await submit({
      formType: 'contact',
      subject: 'trial',
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      company: formData.company,
      message: `Discovery Trial request. Primary AI platform: ${formData.platform}`,
      website: formData.website,
    });
    if (success) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        platform: PLATFORM_OPTIONS[0],
        website: '',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Discovery Trial - CriticalBridge.AI</title>
        <meta
          name="description"
          content="See every agent you own in 30 days — read-only, reversible. One read-only credential to start; we widen the sweep in waves, and you approve each one."
        />
        <meta
          name="keywords"
          content="shadow agent discovery, ai agent inventory, agentic ai risk, criticalbridge discovery trial"
        />
        <meta property="og:title" content="Discovery Trial - CriticalBridge.AI" />
        <meta
          property="og:description"
          content="See every agent you own in 30 days — read-only, reversible."
        />
        <link rel="canonical" href="https://www.criticalbridge.ai/trial" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Discovery Trial
          </div>
          <h1 className="mt-4 max-w-[26ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl">
            See every agent you own in 30 days. Read-only. Reversible.
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            One read-only credential to start. We widen the sweep in waves, and you approve each one
            — so your estate comes into focus without a single change to your environment.
          </p>
        </div>
      </section>

      {/* Three waves */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            The 30 days
          </span>
          <h2 className="mt-2 text-3xl font-bold">Three waves. You approve each one.</h2>

          <div className="mt-8 divide-y divide-border">
            {WAVES.map((wave) => (
              <div
                key={wave.n}
                className="grid grid-cols-[56px_1fr] gap-4 py-6 first:pt-0 sm:grid-cols-[64px_1fr]"
              >
                <div className="font-display text-2xl font-extrabold text-primary">{wave.n}</div>
                <div>
                  <h3 className="text-lg font-bold">{wave.title}</h3>
                  <p className="mt-1.5 max-w-[64ch] text-[15.5px] leading-relaxed text-muted-foreground">
                    {wave.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why it's easy to say yes */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Why it's easy to say yes
          </span>
          <h2 className="mt-2 text-3xl font-bold text-secondary-foreground">
            Engineered so security approves it in days, not months.
          </h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {EASY_YES_ITEMS.map((item) => (
              <div
                key={item.title}
                className="rounded-md border border-secondary-foreground/20 p-4"
              >
                <div className="font-semibold text-secondary-foreground">{item.title}</div>
                <div className="mt-1.5 text-sm text-secondary-foreground/60">
                  {item.description}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 max-w-[68ch] text-secondary-foreground/70">
            On day 30 you hold the{' '}
            <strong className="text-secondary-foreground">Shadow Agent Report</strong>: your
            complete agent inventory — enrolled and unknown — risk-ranked with owners, data access,
            and an enrollment plan. Examiner-consumable, and yours to keep whether or not you buy.
          </p>
        </div>
      </section>

      {/* Request form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Request access
          </span>
          <h2 className="mt-2 text-3xl font-bold">Request your Discovery Trial</h2>

          <div className="mt-6 max-w-xl rounded-lg border bg-card p-6 sm:p-7">
            {submitStatus === 'success' && (
              <div className="py-6 text-center">
                <CheckCircle className="mx-auto mb-4 h-14 w-14 text-success" />
                <h3 className="mb-2 text-xl font-bold">Request received!</h3>
                <p className="mb-6 text-muted-foreground">
                  We'll reach out shortly to get your Discovery Trial started.
                </p>
                <Button variant="outline" onClick={reset}>
                  Send another request
                </Button>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="py-6 text-center">
                <AlertCircle className="mx-auto mb-4 h-14 w-14 text-destructive" />
                <h3 className="mb-2 text-xl font-bold">Something went wrong</h3>
                <p className="mb-6 text-muted-foreground">
                  {errorMessage || 'Please try again or email us at sales@criticalbridge.ai'}
                </p>
                <Button onClick={reset}>Try Again</Button>
              </div>
            )}

            {submitStatus === 'idle' && (
              <form className="grid gap-4" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className={labelClass}>
                    First name
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Jane"
                      required
                      disabled={isSubmitting}
                    />
                  </label>
                  <label className={labelClass}>
                    Last name
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Doe"
                      required
                      disabled={isSubmitting}
                    />
                  </label>
                </div>

                <label className={labelClass}>
                  Work email
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="jane@carrier.com"
                    required
                    disabled={isSubmitting}
                  />
                </label>

                <label className={labelClass}>
                  Company
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Carrier Mutual"
                    disabled={isSubmitting}
                  />
                </label>

                <label className={labelClass}>
                  Primary AI platform
                  <select
                    name="platform"
                    value={formData.platform}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isSubmitting}
                  >
                    {PLATFORM_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Request the trial'
                  )}
                </Button>

                <p className="text-xs text-muted-foreground">
                  By submitting this form, you agree to our{' '}
                  <Link to="/privacy" className="underline hover:text-primary">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
